//
// Copyright 2024 DXOS.org
//

import { type Extension } from '@codemirror/state';
import { type EditorState } from '@codemirror/state';
import { type EditorView } from '@codemirror/view';
import { type RefObject, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { invariant } from '@dxos/invariant';
import { modalStateEffect } from '@dxos/ui-editor';
import { type MaybePromise } from '@dxos/util';

import { type EditorMenuProviderProps } from './EditorMenuProvider.tsx';
import { type EditorMenuGroup, type EditorMenuItem } from './menu.ts';
import { filterMenuGroups, getMenuItem, getNextMenuItem, getPreviousMenuItem } from './menu.ts';
import { type PopoverOptions, popover, popoverRangeEffect, popoverStateField } from './popover.ts';

export type GetMenuContext = {
  state: EditorState;
  pos: number;
  text: string;
  trigger?: string;
};

export type UseEditorMenuProps = {
  filter?: boolean;
  /**
   * Triggers whose menu is rendered as a combobox: the popover owns a search input and the query is
   * typed there rather than into the document.
   */
  // TODO(wittjosiah): Remove? Prefer inline instead.
  searchTriggers?: string[];
  getMenu?: (context: GetMenuContext) => MaybePromise<EditorMenuGroup[]>;
} & Pick<
  PopoverOptions,
  'trigger' | 'triggerKey' | 'placeholder' | 'activateOnTyping' | 'activateOnDelimiters' | 'multiWordTriggers'
>;

type MenuContext = {
  view: EditorView;
  pos: number;
  text: string;
  trigger?: string;
  /** The trigger's query may hold spaces; see {@link PopoverOptions.multiWordTriggers}. */
  multiWord?: boolean;
};

export type UseEditorMenu = {
  groupsRef: RefObject<EditorMenuGroup[]>;
  extension: Extension;
} & Pick<
  EditorMenuProviderProps,
  | 'currentItem'
  | 'open'
  | 'search'
  | 'query'
  | 'onOpenChange'
  | 'onActivate'
  | 'onSelect'
  | 'onCancel'
  | 'onQueryChange'
  | 'onNavigate'
>;

/**
 * ```tsx
 * const { groupsRef, extension, ...menuProps } = useEditorMenu();
 * const { parentRef, viewRef } = useTextEditor({ extensions: [extension] });
 * return (
 *   <EditorMenuProvider getView={() => viewRef.current} groups={groupsRef.current} {...menuProps}>
 *     <div ref={parentRef} />
 *   </EditorMenuProvider>
 * );
 * ```
 */
export const useEditorMenu = ({
  trigger,
  triggerKey,
  placeholder,
  activateOnTyping,
  activateOnDelimiters,
  multiWordTriggers,
  filter = true,
  searchTriggers,
  getMenu,
}: UseEditorMenuProps): UseEditorMenu => {
  const groupsRef = useRef<EditorMenuGroup[]>([]);
  const currentRef = useRef<EditorMenuItem | null>(null);
  const [currentItem, setCurrentItem] = useState<string>();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState(false);
  const [query, setQuery] = useState('');
  const [_, refresh] = useState({});

  // The document range only supplies the trigger-relative prefix (e.g. the second "@" of a block
  // embed); in search mode the rest of the query comes from the popover's input.
  const contextRef = useRef<MenuContext | null>(null);

  // Monotonic token identifying the latest menu query; bumped per request and on close.
  const requestRef = useRef(0);

  // The latest `getMenu`, read when the menu opens: it changes whenever the caller's data does, and the extension
  // built from it would otherwise rebuild the view, blurring the editor mid-keystroke.
  const getMenuRef = useRef(getMenu);
  useLayoutEffect(() => {
    getMenuRef.current = getMenu;
  }, [getMenu]);

  /**
   * Get filtered options.
   */
  const getMenuOptions = useCallback<NonNullable<UseEditorMenuProps['getMenu']>>(
    async ({ text, trigger, ...props }) => {
      const groups = (await getMenuRef.current?.({ text, trigger, ...props })) ?? [];
      // The "@" menu can use "@@" as syntax for block embeds, so it owns its own query filtering.
      return filter && trigger !== '@'
        ? filterMenuGroups(groups, (item) =>
            text ? (item.label as string).toLowerCase().startsWith(text.toLowerCase()) : true,
          )
        : groups;
    },
    [filter],
  );

  const handleOpenChange = useCallback<NonNullable<UseEditorMenu['onOpenChange']>>(
    async ({ view, open }) => {
      invariant(view);
      setOpen(open);
      if (!open) {
        setCurrentItem(undefined);
        setSearch(false);
        setQuery('');
        contextRef.current = null;
        // Invalidate in-flight queries so a late response cannot repopulate the closed menu.
        requestRef.current++;
        groupsRef.current = [];
        currentRef.current = null;
        view.dispatch({
          effects: [popoverRangeEffect.of(null)],
        });
      }

      // TODO(burdon): Possible race condition.
      //  useTextEditor.handleKeyDown will get called after this handler completes.
      requestAnimationFrame(() => {
        view.dispatch({
          effects: [modalStateEffect.of(open)],
        });
      });
    },
    [getMenuOptions],
  );

  /**
   * Recompute the menu and preselect the first item.
   */
  const updateGroups = useCallback(
    async ({ view, pos, text, trigger }: MenuContext) => {
      const request = ++requestRef.current;
      const groups = (await getMenuOptions({ state: view.state, pos, text, trigger })) ?? [];
      // Queries resolve out of order, so a slower earlier one must not overwrite a newer result
      // (or repopulate the menu after it closed).
      if (request !== requestRef.current) {
        return;
      }

      groupsRef.current = groups;
      const firstItem = groupsRef.current.filter((group) => group.items.length > 0)[0]?.items[0];
      if (firstItem) {
        setCurrentItem(firstItem.id);
        currentRef.current = firstItem;
      } else {
        // No matches: clear the selection so Enter falls through instead of
        // selecting a stale item from a previous query.
        setCurrentItem(undefined);
        currentRef.current = null;
      }

      refresh({});
    },
    [getMenuOptions],
  );

  const handleActivate = useCallback<NonNullable<UseEditorMenu['onActivate']>>(
    async ({ view, trigger }) => {
      const item = getMenuItem(groupsRef.current, currentItem);
      if (item) {
        currentRef.current = item;
      }

      if (!open) {
        handleOpenChange({ view, open: true, trigger });
      }
    },
    [open, handleOpenChange],
  );

  const handleSelect = useCallback<NonNullable<UseEditorMenu['onSelect']>>(({ view, item }) => {
    // Delete trigger range (e.g., "/" and any typed filter text).
    const { range } = view.state.field(popoverStateField) ?? {};
    if (range) {
      view.dispatch({ changes: { from: range.from, to: range.to, insert: '' } });
    }
    void item.onSelect?.({ view, head: view.state.selection.main.head });
    view.focus();
  }, []);

  const handleCancel = useCallback<NonNullable<UseEditorMenu['onCancel']>>(({ view }) => {
    // Delete trigger; a multi-word query may be a sentence the user wants to keep.
    const { range, trigger } = view.state.field(popoverStateField) ?? {};
    if (range && trigger && !contextRef.current?.multiWord) {
      view.dispatch({
        changes: { ...range, insert: '' },
      });
    }
  }, []);

  const handleQueryChange = useCallback<NonNullable<UseEditorMenu['onQueryChange']>>(
    (query) => {
      setQuery(query);
      const context = contextRef.current;
      if (context) {
        void updateGroups({ ...context, text: context.text + query });
      }
    },
    [updateGroups],
  );

  const handleNavigate = useCallback<NonNullable<UseEditorMenu['onNavigate']>>((direction) => {
    setCurrentItem((currentItem) => {
      const next =
        direction === 'up'
          ? getPreviousMenuItem(groupsRef.current, currentItem)
          : getNextMenuItem(groupsRef.current, currentItem);
      // A query that matches nothing leaves an empty menu; arrow keys then have nowhere to go.
      if (!next) {
        return currentItem;
      }

      currentRef.current = next;
      return next.id;
    });
  }, []);

  // Array props are compared by CONTENT, not identity: a caller writing `trigger={['#']}` inline
  // hands over a new array every render, and a changed extension destroys and recreates the view —
  // blurring the editor mid-keystroke. The memo reads them back from the key, so the React Compiler keys it the
  // same way.
  const arraysKey = JSON.stringify({ trigger, searchTriggers, activateOnDelimiters, multiWordTriggers });
  const extension = useMemo<Extension>(() => {
    const arrays: Pick<
      UseEditorMenuProps,
      'trigger' | 'searchTriggers' | 'activateOnDelimiters' | 'multiWordTriggers'
    > = JSON.parse(arraysKey);
    return popover({
      trigger: arrays.trigger,
      triggerKey,
      placeholder,
      activateOnTyping,
      activateOnDelimiters: arrays.activateOnDelimiters,
      multiWordTriggers: arrays.multiWordTriggers,
      onClose: ({ view }) => handleOpenChange({ view, open: false }),
      onEnter: ({ view }) => {
        if (currentRef.current) {
          handleSelect({ view, item: currentRef.current });
          return true;
        }
        return false;
      },
      onArrowUp: () => handleNavigate('up'),
      onArrowDown: () => handleNavigate('down'),
      onTextChange: async ({ view, pos, text, trigger }) => {
        const context: MenuContext = {
          view,
          pos,
          text,
          trigger,
          multiWord: !!trigger && !!arrays.multiWordTriggers?.includes(trigger),
        };
        contextRef.current = context;
        // The input is remounted per activation, so the query always starts empty.
        setSearch(!!trigger && !!arrays.searchTriggers?.includes(trigger));
        setQuery('');
        await updateGroups(context);
      },
    });
  }, [handleOpenChange, handleNavigate, updateGroups, arraysKey, triggerKey, placeholder, activateOnTyping]);

  return {
    groupsRef,
    extension,
    currentItem,
    open,
    search,
    query,
    onOpenChange: handleOpenChange,
    onActivate: handleActivate,
    onSelect: handleSelect,
    onCancel: handleCancel,
    onQueryChange: handleQueryChange,
    onNavigate: handleNavigate,
  };
};
